import { PasswordInput } from '@mantine/core';

import { CustomPasswordInputProps } from '../../@types/global';

import WithStrengthMeter from './WithStrengthMeter';

/**
 * This component is a wrapper around Mantine's PasswordInput component.
 * It adds the ability to have a strength meter to the password input field if needed.
 *
 * All props from [Mantine's PasswordInput](https://mantine.dev/core/password-input)
 * component are available. In addition, the following custom props are also available:
 *
 * - `enableStrengthMeter` - Whether to enable the strength meter or not.
 * - `minLength` - The minimum length of the password. Defaults to 8.
 * - `needsLowerCase` - Whether the password needs to have at least one lower case character. Defaults to false.
 * - `needsUpperCase` - Whether the password needs to have at least one upper case character. Defaults to false.
 * - `needsNumber` - Whether the password needs to have at least one number. Defaults to false.
 * - `needsSymbol` - Whether the password needs to have at least one symbol. Defaults to false.
 *
 * Note that the above props are only applicable if `enableStrengthMeter` is set to `true`.
 * They are not used for actual validation logic.
 *
 */

export default function CustomPasswordInput({
  enableStrengthMeter = false,
  ...rest
}: CustomPasswordInputProps) {
  return enableStrengthMeter ? (
    <WithStrengthMeter {...rest} />
  ) : (
    <PasswordInput {...rest} />
  );
}
